/**
 * PatentDraftPane — Multi-step DPA generation workspace pane.
 *
 * Provides tabs for each workflow step (Setup, Structure, Draft, Title/Abstract, Audit, Export).
 * Displayed as a tab in WorkspacePage when field === 'patent_application'.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usePatentDraftStore } from '../stores/patentDraftStore';
import { patentDraftService, type Embodiment, type ReferenceSign, type PatentDraft, type DPACostEstimate, type StepResult, type ClaimSupportFlag } from '../services/patentDraftService';

interface Props {
  subProjectId: string;
  solutionId: string;
}

type Tab = 'setup' | 'structure' | 'draft' | 'title_abstract' | 'audit' | 'export';

const PatentDraftPane: React.FC<Props> = ({ subProjectId, solutionId }) => {
  const { t } = useTranslation();
  const {
    draft,
    costEstimate,
    stepResult,
    isRunningStep,
    currentStep,
    error,
    loadDraft,
    estimateCost,
    runStep,
    approveStructure,
    updateContent,
  } = usePatentDraftStore();

  const [activeTab, setActiveTab] = useState<Tab>('setup');

  useEffect(() => {
    loadDraft(subProjectId, solutionId);
  }, [subProjectId, solutionId, loadDraft]);

  const handleEstimate = useCallback(() => {
    estimateCost(subProjectId, solutionId);
  }, [subProjectId, solutionId, estimateCost]);

  const handleRunStep = useCallback(
    (step: string) => {
      runStep(subProjectId, solutionId, step as Parameters<typeof runStep>[2]);
    },
    [subProjectId, solutionId, runStep],
  );

  const handleDownloadDescription = useCallback(async () => {
    try {
      const blob = await patentDraftService.downloadDescription(subProjectId, solutionId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DPA_description_${solutionId.slice(0, 8)}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed', e);
    }
  }, [subProjectId, solutionId]);

  const handleDownloadFigures = useCallback(async () => {
    try {
      const blob = await patentDraftService.downloadFigures(subProjectId, solutionId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DPA_figures_${solutionId.slice(0, 8)}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed', e);
    }
  }, [subProjectId, solutionId]);

  const tabs: Array<{ id: Tab; label: string }> = [
    { id: 'setup', label: t('patentDraft.tabs.setup', 'Setup') },
    { id: 'structure', label: t('patentDraft.tabs.structure', 'Structure') },
    { id: 'draft', label: t('patentDraft.tabs.draft', 'Description') },
    { id: 'title_abstract', label: t('patentDraft.tabs.titleAbstract', 'Title & Abstract') },
    { id: 'audit', label: t('patentDraft.tabs.audit', 'Audit') },
    { id: 'export', label: t('patentDraft.tabs.export', 'Export') },
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Tab bar */}
      <div className="flex items-center gap-0 border-b border-slate-200 bg-white flex-shrink-0 px-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Error display */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-red-700 text-xs">
          {error}
        </div>
      )}

      {/* Loading spinner during step execution */}
      {isRunningStep && (
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 text-blue-700 text-xs flex items-center gap-2">
          <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          {t('patentDraft.running', 'Running step {{step}}...', { step: currentStep })}
        </div>
      )}

      {/* Tab content */}
      <div className="flex-1 overflow-auto p-4">
        {activeTab === 'setup' && (
          <SetupTab
            costEstimate={costEstimate}
            onEstimate={handleEstimate}
            onRunStructure={() => handleRunStep('step2')}
            isRunning={isRunningStep}
            stepResult={stepResult}
          />
        )}

        {activeTab === 'structure' && (
          <StructureTab
            draft={draft}
            stepResult={stepResult}
            isRunning={isRunningStep}
            onRunRefSigns={() => handleRunStep('step3')}
            onApprove={(embs, signs) => approveStructure(subProjectId, solutionId, embs, signs)}
          />
        )}

        {activeTab === 'draft' && (
          <DraftTab
            draft={draft}
            isRunning={isRunningStep}
            onRunEmbodiments={() => handleRunStep('step5_1')}
            onRunBriefDrawings={() => handleRunStep('step5_2')}
            onRunField={() => handleRunStep('step5_3')}
            onRunBackground={() => handleRunStep('step5_4')}
            onRunSummary={() => handleRunStep('step5_5')}
          />
        )}

        {activeTab === 'title_abstract' && (
          <TitleAbstractTab
            draft={draft}
            isRunning={isRunningStep}
            onGenerate={() => handleRunStep('step6')}
            onUpdate={(title, abstract) =>
              updateContent(subProjectId, solutionId, { title, abstract })
            }
          />
        )}

        {activeTab === 'audit' && (
          <AuditTab
            draft={draft}
            isRunning={isRunningStep}
            onRunCoherence={() => handleRunStep('step7')}
          />
        )}

        {activeTab === 'export' && (
          <ExportTab
            draft={draft}
            onDownloadDescription={handleDownloadDescription}
            onDownloadFigures={handleDownloadFigures}
          />
        )}
      </div>
    </div>
  );
};

// ── Sub-components per tab ────────────────────────────────────────────────────

const SetupTab: React.FC<{
  costEstimate: DPACostEstimate | null;
  onEstimate: () => void;
  onRunStructure: () => void;
  isRunning: boolean;
  stepResult: StepResult | null;
}> = ({ costEstimate, onEstimate, onRunStructure, isRunning, stepResult: _stepResult }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-4 max-w-2xl">
      <h3 className="text-sm font-semibold text-slate-700">
        {t('patentDraft.setup.title', 'Step 1: Setup & Cost Estimate')}
      </h3>
      <p className="text-xs text-slate-500">
        {t('patentDraft.setup.description', 'Review inputs and estimate the cost of generating the Draft Patent Application.')}
      </p>

      <button
        onClick={onEstimate}
        disabled={isRunning}
        className="px-3 py-1.5 text-xs font-medium bg-white border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
      >
        {t('patentDraft.setup.estimateCost', 'Estimate Cost')}
      </button>

      {costEstimate && (
        <div className="border border-slate-200 rounded p-3 bg-slate-50 text-xs space-y-1">
          <div><span className="font-medium">Figures:</span> {costEstimate.num_figures}</div>
          <div><span className="font-medium">Claims:</span> {costEstimate.num_claims}</div>
          <div><span className="font-medium">Estimated embodiments:</span> {costEstimate.num_embodiments_estimated}</div>
          <div><span className="font-medium">Model:</span> {costEstimate.model_name || costEstimate.model_id}</div>
          <div className="pt-1 border-t border-slate-200 font-semibold">
            Estimated cost: {(costEstimate.estimated_cost_cents / 100).toFixed(2)} {costEstimate.currency}
          </div>
        </div>
      )}

      <button
        onClick={onRunStructure}
        disabled={isRunning}
        className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
      >
        {t('patentDraft.setup.generateStructure', 'Generate Structure (Step 2)')}
      </button>
    </div>
  );
};

const StructureTab: React.FC<{
  draft: PatentDraft | null;
  stepResult: StepResult | null;
  isRunning: boolean;
  onRunRefSigns: () => void;
  onApprove: (embs: Embodiment[], signs: ReferenceSign[]) => void;
}> = ({ draft, stepResult, isRunning, onRunRefSigns, onApprove }) => {
  const { t } = useTranslation();
  const embodiments = draft?.embodiments || [];
  const referenceSigns = draft?.reference_signs || [];

  return (
    <div className="space-y-4 max-w-3xl">
      <h3 className="text-sm font-semibold text-slate-700">
        {t('patentDraft.structure.title', 'Steps 2-4: Structure & Reference Signs')}
      </h3>

      {embodiments.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-slate-600">Embodiments</h4>
          {embodiments.map((emb: Embodiment) => (
            <div key={emb.number} className="border border-slate-200 rounded p-2 text-xs bg-white">
              <div className="font-medium">Embodiment {emb.number}: {emb.characterizing_feature}</div>
              <div className="text-slate-500">Figures: {emb.figure_ids.join(', ') || 'none'}</div>
              <div className="text-slate-500">Claims: {emb.claim_numbers.join(', ') || 'none'}</div>
              {emb.needs_additional_figures && (
                <div className="text-amber-600 mt-1">
                  ⚠ Additional figure(s) needed: {emb.missing_figure_descriptions.join('; ')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {referenceSigns.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-xs font-medium text-slate-600">Reference Signs</h4>
          <table className="text-xs w-full border-collapse">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-1 px-2">Sign</th>
                <th className="text-left py-1 px-2">Component</th>
                <th className="text-left py-1 px-2">Figures</th>
              </tr>
            </thead>
            <tbody>
              {referenceSigns.map((s: ReferenceSign, i: number) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-1 px-2 font-mono">{s.sign}</td>
                  <td className="py-1 px-2">{s.component}</td>
                  <td className="py-1 px-2 text-slate-500">{s.figure_ids.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={onRunRefSigns}
          disabled={isRunning || embodiments.length === 0}
          className="px-3 py-1.5 text-xs font-medium bg-white border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
        >
          {t('patentDraft.structure.generateRefSigns', 'Generate Reference Signs (Step 3)')}
        </button>
        <button
          onClick={() => onApprove(embodiments, referenceSigns)}
          disabled={isRunning || embodiments.length === 0}
          className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
        >
          {t('patentDraft.structure.approve', 'Approve Structure (Step 4)')}
        </button>
      </div>

      {stepResult?.content && (
        <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-3 whitespace-pre-wrap max-h-60 overflow-auto">
          {stepResult.content}
        </pre>
      )}
    </div>
  );
};

const DraftTab: React.FC<{
  draft: PatentDraft | null;
  isRunning: boolean;
  onRunEmbodiments: () => void;
  onRunBriefDrawings: () => void;
  onRunField: () => void;
  onRunBackground: () => void;
  onRunSummary: () => void;
}> = ({ draft, isRunning, onRunEmbodiments, onRunBriefDrawings, onRunField, onRunBackground, onRunSummary }) => {
  const { t } = useTranslation();
  const descriptionHtml = draft?.description_html || '';

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-slate-700">
        {t('patentDraft.draft.title', 'Step 5: Generate Description Sections')}
      </h3>

      <div className="flex flex-wrap gap-2">
        <button onClick={onRunField} disabled={isRunning}
          className="px-3 py-1.5 text-xs bg-white border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50">
          5.3 Field of Invention
        </button>
        <button onClick={onRunBackground} disabled={isRunning}
          className="px-3 py-1.5 text-xs bg-white border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50">
          5.4 Background
        </button>
        <button onClick={onRunSummary} disabled={isRunning}
          className="px-3 py-1.5 text-xs bg-white border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50">
          5.5 Summary
        </button>
        <button onClick={onRunBriefDrawings} disabled={isRunning}
          className="px-3 py-1.5 text-xs bg-white border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50">
          5.2 Brief Drawings
        </button>
        <button onClick={onRunEmbodiments} disabled={isRunning}
          className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
          5.1 Detailed Description
        </button>
      </div>

      {descriptionHtml && (
        <div className="border border-slate-200 rounded bg-white p-4 text-sm prose prose-sm max-w-none max-h-[60vh] overflow-auto"
          dangerouslySetInnerHTML={{ __html: descriptionHtml }}
        />
      )}

      {!descriptionHtml && (
        <p className="text-xs text-slate-400 italic">
          {t('patentDraft.draft.empty', 'No description generated yet. Run the steps above.')}
        </p>
      )}
    </div>
  );
};

const TitleAbstractTab: React.FC<{
  draft: PatentDraft | null;
  isRunning: boolean;
  onGenerate: () => void;
  onUpdate: (title: string, abstract: string) => void;
}> = ({ draft, isRunning, onGenerate, onUpdate }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(draft?.title || '');
  const [abstract, setAbstract] = useState(draft?.abstract || '');

  useEffect(() => {
    setTitle(draft?.title || '');
    setAbstract(draft?.abstract || '');
  }, [draft?.title, draft?.abstract]);

  return (
    <div className="space-y-4 max-w-2xl">
      <h3 className="text-sm font-semibold text-slate-700">
        {t('patentDraft.titleAbstract.title', 'Step 6: Title & Abstract')}
      </h3>

      <button onClick={onGenerate} disabled={isRunning}
        className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
        {t('patentDraft.titleAbstract.generate', 'Generate Title & Abstract')}
      </button>

      <div className="space-y-2">
        <label className="text-xs font-medium text-slate-600">Title</label>
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded"
        />
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium text-slate-600">
          Abstract <span className="text-slate-400">(max 150 words / 1000 chars)</span>
        </label>
        <textarea
          value={abstract}
          onChange={e => setAbstract(e.target.value)}
          maxLength={1000}
          rows={5}
          className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded resize-y"
        />
        <div className="text-xs text-slate-400">
          {abstract.split(/\s+/).filter(Boolean).length} words / {abstract.length} chars
        </div>
      </div>

      <button
        onClick={() => onUpdate(title, abstract)}
        disabled={isRunning}
        className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
      >
        {t('common.save', 'Save')}
      </button>
    </div>
  );
};

const AuditTab: React.FC<{
  draft: PatentDraft | null;
  isRunning: boolean;
  onRunCoherence: () => void;
}> = ({ draft, isRunning, onRunCoherence }) => {
  const { t } = useTranslation();
  const audit = draft?.support_audit || [];

  return (
    <div className="space-y-4 max-w-3xl">
      <h3 className="text-sm font-semibold text-slate-700">
        {t('patentDraft.audit.title', 'Step 7: Coherence & Claim Support')}
      </h3>

      <button onClick={onRunCoherence} disabled={isRunning}
        className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
        {t('patentDraft.audit.run', 'Run Coherence Check & Claim Audit')}
      </button>

      {audit.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-xs font-medium text-slate-600">Claim Support Audit</h4>
          <table className="text-xs w-full border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left py-1 px-2">Claim</th>
                <th className="text-left py-1 px-2">Feature</th>
                <th className="text-left py-1 px-2">Supported</th>
                <th className="text-left py-1 px-2">Note</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((flag: ClaimSupportFlag, i: number) => (
                <tr key={i} className={`border-b border-slate-100 ${!flag.supported ? 'bg-red-50' : ''}`}>
                  <td className="py-1 px-2">{flag.claim_number}</td>
                  <td className="py-1 px-2">{flag.feature_text}</td>
                  <td className="py-1 px-2">{flag.supported ? '✓' : '✗'}</td>
                  <td className="py-1 px-2 text-slate-500">{flag.note || flag.supporting_paragraph || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {audit.length === 0 && (
        <p className="text-xs text-slate-400 italic">
          {t('patentDraft.audit.empty', 'No audit results yet. Run the coherence check.')}
        </p>
      )}
    </div>
  );
};

const ExportTab: React.FC<{
  draft: PatentDraft | null;
  onDownloadDescription: () => void;
  onDownloadFigures: () => void;
}> = ({ draft, onDownloadDescription, onDownloadFigures }) => {
  const { t } = useTranslation();
  const hasContent = !!draft?.description_html;

  return (
    <div className="space-y-4 max-w-2xl">
      <h3 className="text-sm font-semibold text-slate-700">
        {t('patentDraft.export.title', 'Export Draft Patent Application')}
      </h3>

      <div className="flex gap-3">
        <button
          onClick={onDownloadDescription}
          disabled={!hasContent}
          className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {t('patentDraft.export.description', 'Description Export (.docx)')}
        </button>
        <button
          onClick={onDownloadFigures}
          disabled={!draft?.embodiments?.length}
          className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {t('patentDraft.export.figures', 'Figures Export (.docx)')}
        </button>
      </div>

      {!hasContent && (
        <p className="text-xs text-slate-400 italic">
          {t('patentDraft.export.noContent', 'Generate the description first before exporting.')}
        </p>
      )}
    </div>
  );
};

export default PatentDraftPane;
